// A minimal cached query layer: one in-flight request per key, results shared
// by every component that reads the key, optional polling, and invalidation
// by key prefix after a mutation. Deliberately tiny: the app has no data
// library and these screens need nothing more.
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { ApiError } from "./client";

interface Entry<T> {
  data?: T;
  error?: ApiError;
  loading: boolean;
  promise?: Promise<void>;
  updatedAt?: number;
}

const cache = new Map<string, Entry<unknown>>();
const listeners = new Map<string, Set<() => void>>();

function emit(key: string) {
  listeners.get(key)?.forEach(listener => listener());
}

function entry<T>(key: string): Entry<T> {
  if (!cache.has(key)) cache.set(key, { loading: false });
  return cache.get(key) as Entry<T>;
}

function load<T>(key: string, fetcher: () => Promise<T>): Promise<void> {
  const current = entry<T>(key);
  if (current.promise) return current.promise;
  cache.set(key, { ...current, loading: true });
  emit(key);
  const promise = fetcher().then(
    data => { cache.set(key, { data, loading: false, updatedAt: Date.now() }); },
    error => {
      const apiError = error instanceof ApiError ? error : new ApiError(0, "UNKNOWN", String(error?.message || error));
      cache.set(key, { ...entry<T>(key), error: apiError, loading: false, promise: undefined });
    },
  ).finally(() => { emit(key); });
  cache.set(key, { ...entry<T>(key), promise });
  return promise;
}

/** Refetch every cached key that starts with one of `prefixes`. */
export function invalidate(...prefixes: string[]) {
  for (const key of [...cache.keys()]) {
    if (prefixes.some(prefix => key.startsWith(prefix))) {
      const current = cache.get(key)!;
      cache.set(key, { ...current, promise: undefined });
      if (listeners.get(key)?.size) void load(key, fetchers.get(key)!);
    }
  }
}

const fetchers = new Map<string, () => Promise<unknown>>();

export interface Resource<T> {
  data: T | undefined;
  error: ApiError | undefined;
  /** True only before the first result: background refreshes keep showing data. */
  loading: boolean;
  refreshing: boolean;
  reload: () => void;
}

export function useResource<T>(key: string | null, fetcher: () => Promise<T>, options: { pollMs?: number } = {}): Resource<T> {
  if (key) fetchers.set(key, fetcher);
  const subscribe = useCallback((listener: () => void) => {
    if (!key) return () => {};
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key)!.add(listener);
    return () => { listeners.get(key)?.delete(listener); };
  }, [key]);
  const snapshot = useSyncExternalStore(subscribe, () => (key ? cache.get(key) : undefined));

  useEffect(() => {
    if (!key) return;
    const current = entry<T>(key);
    if (current.data === undefined && !current.promise) void load(key, fetchers.get(key) as () => Promise<T>);
  }, [key]);

  useEffect(() => {
    if (!key || !options.pollMs) return;
    const timer = window.setInterval(() => {
      const current = entry<T>(key);
      if (!current.promise && document.visibilityState === "visible") {
        cache.set(key, { ...current, promise: undefined });
        void load(key, fetchers.get(key) as () => Promise<T>);
      }
    }, options.pollMs);
    return () => window.clearInterval(timer);
  }, [key, options.pollMs]);

  const current = snapshot as Entry<T> | undefined;
  return {
    data: current?.data,
    error: current?.error,
    loading: !!key && current?.data === undefined && !current?.error,
    refreshing: !!current?.loading && current?.data !== undefined,
    reload: () => key && invalidate(key),
  };
}

/** Run a mutation and report its pending/error state to the caller. */
export function useMutation<A extends unknown[], R>(action: (...args: A) => Promise<R>) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const run = useCallback(async (...args: A): Promise<R | undefined> => {
    setPending(true);
    setError(null);
    try {
      return await action(...args);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(0, "UNKNOWN", String(caught)));
      return undefined;
    } finally {
      setPending(false);
    }
  }, [action]);
  return { run, pending, error, reset: () => setError(null) };
}
